import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";
import { useLang } from "@/context/AppContext";
import { useApi } from "@/lib/useApi";

const MARGIN = { top: 8, right: 12, bottom: 0, left: -20 };
const Y_DOMAIN = [0, 100];
const CURSOR = { stroke: "rgba(255,255,255,0.1)" };
const ACTIVE_DOT = { r: 6 };

const reasonLabel = (t, r) => {
  if (r === "created") return t("trust_created");
  const [kind, val] = r.split(":");
  if (kind === "outcome") return `${t("r_outcome")}: ${t(`oc_${val}`)}`;
  if (kind === "interview_rating") return `${t("r_interview_rating")}: ${val}★`;
  return r;
};

const Tip = ({ active, payload, t }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-white/10 bg-[#0B0F17] px-3 py-2 text-xs" data-testid="trust-tooltip">
      <p className="font-display text-lg font-bold text-white">{p.value}{p.delta ? <span className={`ml-2 font-mono text-xs ${p.delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>{p.delta > 0 ? "+" : ""}{p.delta}</span> : null}</p>
      <p className="text-slate-300">{reasonLabel(t, p.reason)}</p>
      <p className="font-mono text-[10px] text-slate-500">{new Date(p.at).toLocaleString()}</p>
    </div>
  );
};

export const TrustTimeline = ({ pid }) => {
  const { t } = useLang();
  const { data: d } = useApi(`/profiles/${pid}/trust-timeline`);
  if (!d) return null;
  const data = d.points.map((p, i) => ({ ...p, i, label: new Date(p.at).toLocaleDateString() }));
  const changes = d.points.length - 1;
  return (
    <section className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="trust-timeline">
      <div className="mb-4 flex items-center justify-between">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400"><TrendingUp className="h-4 w-4" />{t("trust_timeline")}</p>
        <span className="font-mono text-[11px] text-slate-500" data-testid="trust-timeline-count">{changes}</span>
      </div>
      {changes === 0 ? <p className="text-sm text-slate-500" data-testid="trust-timeline-empty">{t("no_trust_changes")}</p> : (
        <div className="h-56" data-testid="trust-timeline-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={MARGIN}>
              <XAxis dataKey="i" tickFormatter={(i) => data[i]?.label} stroke="#475569" fontSize={10} tickLine={false} axisLine={false} minTickGap={30} />
              <YAxis domain={Y_DOMAIN} stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
              <ReferenceLine y={75} stroke="#EAB308" strokeDasharray="4 4" strokeOpacity={0.5} />
              <Tooltip content={<Tip t={t} />} cursor={CURSOR} />
              <Line type="stepAfter" dataKey="value" stroke="#10B981" strokeWidth={2}
                dot={({ cx, cy, payload }) => <circle key={payload.i} cx={cx} cy={cy} r={3.5} fill={payload.delta < 0 ? "#F43F5E" : "#10B981"} stroke="#0B0F17" strokeWidth={1.5} />}
                activeDot={ACTIVE_DOT} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};
